import React, { useState } from 'react'
import { AlertCircle, CheckCircle, Heart, Users, Clock, Layers, Building2 } from 'lucide-react'
import { useNavigationStore } from '@/stores/navigation'
import DownloadForm from '@/components/common/DownloadForm'

const CaseStudyCHOC: React.FC = () => {
  const navigationStore = useNavigationStore()
  const handleBookDemo = () => navigationStore.openDemoModal()
  const [downloadFormOpen, setDownloadFormOpen] = useState(false)

  return (
    <main className="min-h-screen w-full bg-white text-slate-900">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-emerald-50/30 to-indigo-50" />
        <div className="relative mx-auto max-w-6xl px-6 py-16 md:py-24">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <h1 className="text-3xl md:text-5xl font-semibold leading-tight">How CHOC Children’s Unified Pediatric Referrals with EceptionistCX</h1>
              <p className="mt-4 text-lg md:text-xl text-slate-700">ReferralCX centralized thousands of monthly specialty referrals across CHOC’s regional pediatric network—improving speed, visibility, and coordination.</p>
              <div className="mt-8 flex gap-3">
                <button 
                  onClick={() => setDownloadFormOpen(true)} 
                  className="rounded-2xl px-6 py-3 bg-slate-900 text-white hover:bg-slate-800 transition"
                >
                  Download Full Case Study
                </button>
                <button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 transition">Book a Demo</button>
              </div>
            </div>
            <div className="h-64 md:h-80 rounded-3xl ring-1 ring-slate-200 p-6 flex items-center bg-white/90">
              <div className="w-full">
                <div className="text-lg md:text-xl font-semibold uppercase tracking-wide text-slate-700">Tools Used</div>
                <div className="mt-4 space-y-3">
                  <div className="leading-none">
                    <span className="lowercase font-normal text-4xl md:text-5xl text-[#0054A6]">referral</span>
                    <span className="font-bold uppercase text-4xl md:text-5xl text-[#7AC043]">CX</span>
                    <span className="align-top ml-1 text-xs md:text-sm font-normal text-slate-600">TM</span>
                  </div>
                </div>
                <div className="mt-4 text-sm text-slate-600">Bi-directional integrations (HL7/APIs) + fax ingestion; real-time referral tracking and closed-loop management.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 -mt-10 md:-mt-14">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Building2 className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">Pediatric network</div>
              <div className="text-slate-600">Specialty care across CHOC</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Layers className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">4 counties • 2 hospitals</div>
              <div className="text-slate-600">Regional coverage & access</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Users className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">30+ divisions</div>
              <div className="text-slate-600">Pediatric subspecialties</div>
            </div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-5 text-center">
              <Clock className="mx-auto h-8 w-8 text-slate-700" />
              <div className="mt-2 text-2xl font-semibold">100+ coordinators</div>
              <div className="text-slate-600">Unified on ReferralCX</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50/60 mt-10 md:mt-16 py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <h2 className="text-2xl md:text-3xl font-semibold">The Challenge</h2>
            <p className="mt-4 text-slate-700">High-volume referrals from community providers and internal divisions arrived via fax/email with limited tracking—slowing pediatric access and obscuring status.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Manual, multi-channel intake with duplicate data entry</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Low visibility for clinicians and coordinators into referral progress</span></li>
              <li className="flex gap-3 items-start"><AlertCircle className="h-5 w-5 text-red-500" /><span>Leakage risk across a large pediatric network</span></li>
            </ul>
          </div>
          <div className="h-60 md:h-full rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6">
            <div className="text-sm uppercase tracking-wider text-slate-500">Operational Pain Points</div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Fax & email referrals</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Duplicate entry</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">No real-time status</span>
              <span className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-sm bg-slate-50">Network leakage</span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Solution</h2>
          <p className="mt-4 text-slate-700">CHOC deployed ReferralCX to centralize intake, integrate with EHR systems (HL7/APIs) and fax, and provide a closed-loop referral workflow with real-time tracking.</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Centralized intake</div><div className="mt-1 text-slate-600">Unified referrals from internal divisions & community providers.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Bi-directional integration</div><div className="mt-1 text-slate-600">HL7/APIs + fax ingestion to reduce manual entry.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Closed-loop management</div><div className="mt-1 text-slate-600">Real-time status, alerts, and leakage prevention.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Coordinator workflows</div><div className="mt-1 text-slate-600">Queues, worklists, and bulk actions for 100+ staff.</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold text-lg">Clinician visibility</div><div className="mt-1 text-slate-600">Referral data surfaced at point of care.</div></div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 text-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">The Results</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Faster pediatric access</strong> as referrals are triaged and scheduled sooner.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Leakage reduced</strong> through closed-loop tracking and network visibility.</span></div>
            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-5 flex items-start gap-3"><CheckCircle className="h-5 w-5 text-green-400" /><span><strong>Higher coordinator productivity</strong> with queues and bulk actions.</span></div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <h2 className="text-2xl md:text-3xl font-semibold">Human Impact</h2>
            <p className="mt-4 text-slate-700">Families across four counties get clearer answers and faster access to pediatric specialists—closer to home.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Less waiting; fewer duplicate phone calls and faxes</span></li>
              <li className="flex gap-3 items-start"><Heart className="h-5 w-5 text-rose-500" /><span>Better coordination between referring providers and CHOC clinics</span></li>
            </ul>
          </div>
          <aside className="rounded-3xl bg-slate-50 ring-1 ring-slate-200 p-6">
            <blockquote className="text-lg leading-relaxed">“ReferralCX gave us real-time visibility and helped our teams keep families moving through the system.”</blockquote>
            <div className="mt-3 text-slate-600">— Referral Operations Leadership, CHOC</div>
          </aside>
        </div>
      </section>

      <section className="bg-white py-12 md:py-16">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-2xl md:text-3xl font-semibold">Looking Back</h2>
          <p className="mt-4 text-slate-700">Launched Dec 2018, ReferralCX established a scalable referral backbone for CHOC’s pediatric network—spanning two hospitals and dozens of clinics.</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Speed to value</div><div className="text-slate-600 mt-1">Rapid adoption across 30+ divisions</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Network scalability</div><div className="text-slate-600 mt-1">Supports 100+ referral coordinators</div></div>
            <div className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 p-6"><div className="font-semibold">Data-driven</div><div className="text-slate-600 mt-1">Real-time referral insights at point of care</div></div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-tr from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="mx-auto max-w-6xl px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-semibold">Ready to transform pediatric access and coordination?</h2>
          <p className="mt-3 text-slate-300">See how EceptionistCX can streamline complex referral workflows across your network.</p>
          <div className="mt-8"><button onClick={handleBookDemo} className="rounded-2xl px-6 py-3 bg-white text-slate-900 hover:bg-slate-100 transition">Book a Demo</button></div>
        </div>
      </section>

      <div className="h-6" />
      
      <DownloadForm
        isOpen={downloadFormOpen}
        onClose={() => setDownloadFormOpen(false)}
        paperTitle="CHOC Children's Case Study"
        pdfPath="/PDF/Detailed Case Studies/Eceptionist_Case+Study_CHOC+Childrens.pdf"
      />
    </main>
  )
}

export default CaseStudyCHOC


